import cn from 'classnames';
import { useContext } from 'react';
import { useMatch } from 'react-router';

import Button from 'components/common/Button';
import Heading from 'components/common/Heading';
import Text from 'components/common/Text';
import { VoucherContext, UserContext } from 'components/signUpFunnel/Contexts';
import AmexPromoApplied from 'components/signUpFunnel/promo/AmexPromoApplied';
import PromoSkeleton from 'components/signUpFunnel/promo/PromoSkeleton';
import Disclaimer from 'components/signUpFunnel/shared/Disclaimer';
import { TEMPO, BrandContext } from 'context/BrandContext';
import * as Routes from 'helpers/routes';
import useAnalyticsCallback from 'hooks/useAnalyticsCallback';
import useBrand from 'hooks/useBrand';
import useFunnelExperimentAssignment from 'hooks/useFunnelExperimentAssignment';
import usePartnershipQuery from 'hooks/usePartnershipQuery';
import { AMEXBCE_PROMO_CODE, GIADA_PROMO_CODES } from 'lib/constants';

const tempoCodes = ['V1TEMPOFB50FS', 'TEMPOGA50FSCNQV1', 'TEMPOGA50FSNBRDV1'];

const tempoPromoAppliedTextOverwrite =
  'Your promotion will be broken out over your 5 upcoming orders: 50% + free shipping, then free shipping on your next 4 orders.';

const PromoApplied = ({
  containerStyles = '',
  hideChangePromoAction = false,
  isAmexUser = false,
  isCourtesyVoucher = false,
  isUsaa = false,
  showHeading = false,
  setHasPromo,
  setErrorMessage,
  promoTextStyles,
}) => {
  const { voucher, promoAppliedText } = useContext(VoucherContext);
  const { brand } = useContext(BrandContext);
  const { user } = useContext(UserContext);
  const isPromoPage = useMatch(Routes.PROMO_PATH);
  const isTempo = useBrand(TEMPO);
  const { data: partnershipData } = usePartnershipQuery(user?.partnerReferrer);
  const partnershipPromotionalText =
    voucher?.partnershipPromotionalText || partnershipData?.partnership?.promotionalText;
  const experimentAssignment = useFunnelExperimentAssignment();

  const handleChangePromoClickWithAnalytics = useAnalyticsCallback(
    null,
    ({ logEvent, events }) => {
      if (brand) {
        logEvent(events.signUp[brand].promo.newSignUpPromoChanged, experimentAssignment);
      }
    },
    [brand, experimentAssignment]
  );

  if (!voucher && !isAmexUser) return <PromoSkeleton />;

  return (
    <>
      {voucher && voucher.code !== AMEXBCE_PROMO_CODE && (
        <div className={containerStyles}>
          {showHeading && (
            <Heading brandStylesOverrides="h2 text-primary" className="mb-4 text-center">
              {isCourtesyVoucher ? (
                <>{voucher.formattedDiscountString} Off Your First Box</>
              ) : (
                <>{isUsaa && 'USAA '} Your discount details</>
              )}
            </Heading>
          )}
          {isCourtesyVoucher && (
            <Text brandStylesOverrides="textMd" className="mb-6">
              The promo code you entered isn't valid, but no worries—we've applied a special offer
              just for you!
            </Text>
          )}
          <Text className={cn('pb-4', promoTextStyles)}>
            {tempoCodes.includes(voucher.code) ? tempoPromoAppliedTextOverwrite : promoAppliedText}
            {partnershipPromotionalText &&
              !GIADA_PROMO_CODES.includes(voucher.code) &&
              ` ${partnershipPromotionalText}`}
          </Text>
          <div className="flex flex-col">
            {!isUsaa && voucher?.disclaimerMessage && (
              <div className="pb-4 text-center">
                <Disclaimer message={voucher?.disclaimerMessage} />
              </div>
            )}
            {!hideChangePromoAction && (
              <Button
                type="button"
                className="border-none bg-transparent pb-4 pt-0 font-normal underline"
                onClick={() => {
                  setErrorMessage(null);
                  setHasPromo(false);
                  handleChangePromoClickWithAnalytics();
                }}
                variation="Secondary"
              >
                {isCourtesyVoucher ? 'Change Promo' : 'Change Promo Code or Gift Card'}
              </Button>
            )}
            {isPromoPage && (
              <div
                className={cn(
                  'mx-auto mt-2 inline-block rounded-lg border border-solid px-3 py-3',
                  {
                    'border-tempo-gray-100 bg-tempo-gray-40': isTempo,
                    'border-cream-110 bg-cream-100 ': !isTempo,
                  }
                )}
              >
                <Text className="text-sm text-charcoal-90">
                  Only one promo code can be applied at a time.
                </Text>
              </div>
            )}
          </div>
        </div>
      )}

      {isAmexUser && (
        <AmexPromoApplied
          setErrorMessage={setErrorMessage}
          setHasPromo={setHasPromo}
          handleChangePromoClickWithAnalytics={handleChangePromoClickWithAnalytics}
        />
      )}
    </>
  );
};

PromoApplied.propTypes = {
  setHasPromo: PropTypes.func.isRequired,
  setErrorMessage: PropTypes.func.isRequired,
  showHeading: PropTypes.bool,
  isUsaa: PropTypes.bool,
  isAmexUser: PropTypes.bool,
  hideChangePromoAction: PropTypes.bool,
  isCourtesyVoucher: PropTypes.bool,
  containerStyles: PropTypes.string,
  promoTextStyles: PropTypes.string,
};

export default PromoApplied;
